import { store, store$ } from '@redux/store';
import { debounceTime, distinctUntilChanged, filter, fromEventPattern, map } from 'rxjs';
import noUiSlider from 'nouislider';
import { searchParametersSlice } from '../redux/slices';
import { selectPrice } from '../redux/selectors';

export default class PriceDualSlider {
  constructor(container) {
    this.onPriceChange$ = store$.pipe(filter(selectPrice), map(selectPrice), distinctUntilChanged());

    // The price range for the slider will be from $0 until $1.5M.
    this.maximumSliderValue = 1500000;
    this.minimumSliderValue = 0;
    this.highPriceLimit = 1500000;

    // Here we have to define the initial al last value of the slider, which is the min and max values.
    // Also, we have to define the slider step value that we have to use according to the selected value.
    // The business rule is from 0 to 500k, we have to use a 50k step, after 500k we have to use 100k step.
    this.rangeConfiguration = {
      min: [this.minimumSliderValue, 50000],
      '33.3%': [500000, 100000],
      max: [this.maximumSliderValue],
    };

    this.setDualRangeSlider(container);
    this.dualRangeSliderValueChange$.subscribe(PriceDualSlider.priceChangeHandler);
    this.onPriceChange$.subscribe(({ minimumPrice, maximumPrice }) =>
      this.updateValueSliders({
        minimumValue: minimumPrice || this.minimumSliderValue,
        maximumValue: maximumPrice || this.maximumSliderValue,
      }),
    );
  }

  /**
   * setDualRangeSlider: Configures all the initial code to make the slider works.
   * @param {HTMLElement} container Element where all the HTML slider elements will be.
   */
  setDualRangeSlider(container) {
    this.minimumSliderLabel = container.querySelector('[data-minimum-slider-label]');
    this.maximumSliderLabel = container.querySelector('[data-maximum-slider-label]');

    this.slider = container.querySelector('[data-slider]');

    this.dualRangeSlider = noUiSlider.create(this.slider, {
      start: [this.minimumSliderValue, this.maximumSliderValue],
      connect: true,
      range: this.rangeConfiguration,
      cssPrefix: 'nhs-c-slider-control__',
    });

    this.setInputEvent();
    this.setChangeEvents();
    this.setDataAttributeTag();
    this.updateSliderLabels();
  }

  /**
   * updateValueSliders: Updates the slider values from an external action.
   * @param {Object} values Object with new values.
   */
  updateValueSliders(values) {
    const { minimumValue, maximumValue } = values;
    this.slider.noUiSlider.set([minimumValue, maximumValue]);
    this.updateSliders();
  }

  /**
   * getLabel: Gets the label to use for the input depending on the value.
   * @param {Number} value Number value to convert.
   * @returns A string with the formatted number.
   * @example XXXk if under $999k, returns $X.XM if over $999k.
   */
  static getLabel(value) {
    if (value < 999000) {
      return `$${value / 1000}k`;
    }
    return `$${value / 1000000}M`;
  }

  /**
   * getSliderValues: Gets the actual slider values.
   * @returns An object with the current slider values.
   */
  getSliderValues() {
    const minimumIndex = 0;
    const maximumIndex = 1;
    const sliderValues = this.dualRangeSlider.get(true);
    const minimumSliderValue = Math.ceil(sliderValues[minimumIndex]);
    const maximumSliderValue = Math.ceil(sliderValues[maximumIndex]);
    const highPriceLimit = this.highPriceLimit;

    return {
      minimumSliderValue,
      maximumSliderValue,
      highPriceLimit,
    };
  }

  /**
   * updateSliderLabels: Updates the label for each slider.
   */
  updateSliderLabels() {
    const { minimumSliderValue, maximumSliderValue } = this.getSliderValues();
    if (minimumSliderValue === this.minimumSliderValue) {
      this.minimumSliderLabel.innerHTML = 'Min';
    } else {
      this.minimumSliderLabel.innerHTML = PriceDualSlider.getLabel(minimumSliderValue);
    }

    if (maximumSliderValue === this.maximumSliderValue) {
      this.maximumSliderLabel.innerHTML = 'Max';
    } else {
      this.maximumSliderLabel.innerHTML = PriceDualSlider.getLabel(maximumSliderValue);
    }
  }

  /**
   * setChangeEvents: Configures the change event that the dual range slider should manage.
   * It refers when the user stopped moving the slider.
   */
  setChangeEvents() {
    this.dualRangeSliderValueChange$ = fromEventPattern(this.getSlideEvent.bind(this)).pipe(
      map(this.getSliderValues.bind(this)),
      debounceTime(500),
      distinctUntilChanged(),
    );
  }

  /**
   * setDataAttributeTag: Adds data attributes to price slider
   */
  setDataAttributeTag() {
    this.slider.querySelector('.nhs-c-slider-control__handle-lower').dataset.qa = 'Minimum_price_slider';
    this.slider.querySelector('.nhs-c-slider-control__handle-lower').dataset.firstFocusableElement = true;
    this.slider.querySelector('.nhs-c-slider-control__handle-upper').dataset.qa = 'Maximum_price_slider';
  }

  /**
   * getSlideEvent: Gets a slide event of the slider. It is called slide because it is as the API uses it.
   * It is very similar to the input event, where it is triggered when the user moved a slider.
   * @param {Function} handler It is a callback that will be used when the slide event is triggered.
   * @returns An event that relates to the slide action.
   */
  getSlideEvent(handler) {
    return this.slider.noUiSlider.on('slide', handler);
  }

  /**
   * updateSliders: Updates the UI for the component.
   */
  updateSliders() {
    this.updateSliderLabels();
  }

  /**
   * setInputEvent: Configures the input events that the dual range slider should manage.
   * It refers each time when the user moves the slider.
   */
  setInputEvent() {
    // Those events are to track actively the movement of the sliders
    fromEventPattern(this.getSlideEvent.bind(this)).subscribe(this.updateSliders.bind(this));
  }

  /**
   * priceChangeHandler: Handles when the price has changed on the store.
   * @param {Object} sliderValues An object with the value to execute the price action.
   */
  static priceChangeHandler(sliderValues) {
    const { minimumSliderValue, maximumSliderValue, highPriceLimit } = sliderValues;

    store.dispatch(
      searchParametersSlice.actions.fetchPrice({
        minimumPrice: minimumSliderValue,
        maximumPrice: maximumSliderValue,
        maximumPriceLimit: highPriceLimit,
      }),
    );
  }
}
